import { ArrowRight, Check, PencilLine, RefreshCw } from 'lucide-react';
import type { InvoicePlanV1 } from '@repo/api-client/dtos';
import type { CurrencyCode } from '@repo/domain';
import { useActiveOrganization } from '@repo/api-client/react';
import { Button } from '@/components/ui/button';
import { displayWireMoney } from '@/features/money';
import { cn } from '@/lib/utils';
import type { CreateInvoiceInput } from './extractArtefacts';

/**
 * Structured preview the assistant emits before any mutation. The
 * user sees the proposed line items + tentative subtotal + client;
 * pressing Accept fires the SDK's `addToolApprovalResponse` which
 * resumes the chat and lets `createInvoice.execute` run server-side.
 *
 * Three terminal states:
 *   • `active` — approval-requested, awaiting Accept.
 *   • `superseded` — a newer proposal exists later in the chat.
 *   • `committed` — `output-available` arrived; show "Created INV-NNN".
 *
 * Mirrors `apps/mobile/features/ai/PlanCard.tsx`'s contract. Pending-
 * state totals are computed client-side from line items only ("before
 * tax" microcopy when any line is taxable); after approval the
 * server-authoritative totals on `resolved.plan` replace it.
 */
export interface PlanCardProps {
  /** Model's createInvoice input — line items, client, dates. */
  input: CreateInvoiceInput;
  /**
   * Server-issued result, present once the SDK's tool-output arrives.
   * When set, the card swaps the action row for a "Created INV-NNN"
   * badge and the totals come from `resolved.plan.totals`.
   */
  resolved: { invoiceId: string; invoiceNumber: string; plan: InvoicePlanV1 } | null;
  onAccept: () => void;
  /**
   * Accept AND open the editor once the invoice exists. The chat
   * surface fires the same SDK approval-response as `onAccept` but
   * also remembers the toolCallId so the resolved tool-output triggers
   * a navigation to the invoice's edit screen. This is the "AI drafts
   * the rough shape, the form handles precision" handoff.
   *
   * Optional — when omitted, only the standalone Accept button shows
   * (current behaviour for screens that don't want the navigation).
   */
  onAcceptAndEdit?: () => void;
  onRefine: () => void;
  /**
   * Disable Accept while we wait for the SDK to round-trip a
   * tool-approval-response (`chat.status === 'submitted' | 'streaming'`
   * on the latest turn).
   */
  busy?: boolean;
  /**
   * `true` when a newer proposal exists later in the chat. The card
   * stays visible for context but hides Accept/Refine.
   */
  superseded?: boolean;
  onOpenInvoice?: (invoiceId: string) => void;
}

export function PlanCard({
  input,
  resolved,
  onAccept,
  onAcceptAndEdit,
  onRefine,
  busy = false,
  superseded = false,
  onOpenInvoice,
}: PlanCardProps) {
  const org = useActiveOrganization();
  // Currency lives on the workspace, not the model's input — keeps
  // the model from ever picking a currency.
  const currency = (org?.currency ?? 'USD') as CurrencyCode;

  const anyTaxable = input.lineItems.some((li) => li.taxable);

  // Pending state shows a tentative subtotal computed from line items
  // alone. Once `resolved` is in we display the authoritative server
  // totals (subtotal/tax/total).
  const tentativeSubtotalMinor = input.lineItems
    .reduce(
      (acc, li) =>
        acc + BigInt(li.unitPrice) * BigInt(Math.round(li.quantity)),
      0n,
    )
    .toString();

  const headerTitle = input.targetInvoiceNumber
    ? `Updates ${input.targetInvoiceNumber} · ${input.clientName}`
    : `Proposed invoice · ${input.clientName}`;

  const issueDateDisplay = formatDateShort(input.issueDate);
  const dueDateDisplay = input.dueDate
    ? formatDateShort(input.dueDate)
    : null;

  return (
    <article className="overflow-hidden rounded-2xl border border-line bg-card shadow-sm shadow-black/5">
      <div className="h-[3px] bg-accent" />

      <header className="flex items-center gap-3 px-4 pb-3 pt-3.5">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-accent-soft text-accent">
          <Check className="size-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-foreground">
            {headerTitle}
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {issueDateDisplay}
            {dueDateDisplay ? ` · Due ${dueDateDisplay}` : null}
          </p>
        </div>
      </header>

      <div className="px-4 pb-2">
        {input.lineItems.map((li, i) => {
          const lineTotal = (
            BigInt(li.unitPrice) * BigInt(Math.round(li.quantity))
          ).toString();
          return (
            <div
              key={`${li.description}-${i}`}
              className={cn(
                'flex items-start gap-3 py-2',
                i > 0 && 'border-t border-line/60',
              )}
            >
              <div className="min-w-0 flex-1">
                <p className="text-[13px] leading-snug text-foreground">
                  {li.description}
                </p>
                <p className="mt-0.5 text-[11px] text-muted-foreground">
                  {li.quantity} × {displayWireMoney(li.unitPrice, currency)}
                </p>
              </div>
              <p className="font-display text-sm font-medium text-foreground tabular-nums">
                {displayWireMoney(lineTotal, currency)}
              </p>
            </div>
          );
        })}
      </div>

      <div className="border-t border-line bg-secondary/40 px-4 py-3">
        {resolved ? (
          <>
            <TotalRow
              label="Subtotal"
              value={displayWireMoney(resolved.plan.totals.subtotal, currency)}
            />
            {resolved.plan.totals.taxTotal !== '0' ? (
              <TotalRow
                label="Tax"
                value={displayWireMoney(resolved.plan.totals.taxTotal, currency)}
              />
            ) : null}
            <TotalRow
              label="Total"
              value={displayWireMoney(resolved.plan.totals.total, currency)}
              bold
            />
          </>
        ) : (
          <>
            <TotalRow
              label="Subtotal"
              value={displayWireMoney(tentativeSubtotalMinor, currency)}
            />
            <TotalRow
              label="Total"
              value={displayWireMoney(tentativeSubtotalMinor, currency)}
              bold
            />
            {anyTaxable ? (
              <p className="mt-1 text-[10px] text-muted-foreground">
                Before tax — final total computed after you accept.
              </p>
            ) : null}
          </>
        )}
      </div>

      {resolved ? (
        <button
          type="button"
          onClick={() => onOpenInvoice?.(resolved.invoiceId)}
          className="flex w-full items-center gap-2.5 border-t border-line px-4 py-3 text-left transition-colors hover:bg-secondary/40"
        >
          <span className="flex size-5 items-center justify-center rounded-full bg-accent text-white">
            <Check className="size-3" />
          </span>
          <span className="flex-1 text-sm font-medium text-foreground">
            Created {resolved.invoiceNumber}
          </span>
          <span className="text-xs text-muted-foreground">View</span>
          <ArrowRight className="size-3.5 text-muted-foreground" />
        </button>
      ) : superseded ? (
        <div className="flex items-center gap-2.5 border-t border-line bg-secondary/40 px-4 py-3">
          <span className="flex size-5 items-center justify-center rounded-full border border-line bg-card text-muted-foreground">
            <RefreshCw className="size-3" />
          </span>
          <p className="text-xs text-muted-foreground">
            Refined below — a newer proposal replaced this one
          </p>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap gap-2 border-t border-line p-2">
            <Button
              type="button"
              variant="outline"
              onClick={onRefine}
              disabled={busy}
              className="flex-1"
            >
              Refine
            </Button>
            {onAcceptAndEdit ? (
              <Button
                type="button"
                variant="outline"
                onClick={onAcceptAndEdit}
                disabled={busy}
                className="flex-1"
                title="Create the draft, then open it in the editor"
              >
                <PencilLine className="size-4" />
                Open in editor
              </Button>
            ) : null}
            <Button
              type="button"
              onClick={onAccept}
              disabled={busy}
              className="flex-[1.4]"
            >
              <Check className="size-4" />
              Accept
            </Button>
          </div>
          <p className="px-4 pb-3 pt-1 text-center text-[10px] text-muted-foreground">
            AI proposes · you approve · nothing is sent without confirmation
          </p>
        </>
      )}
    </article>
  );
}

function TotalRow({
  label,
  value,
  bold,
}: {
  label: string;
  value: string;
  bold?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between py-0.5">
      <span
        className={cn(
          'text-xs',
          bold ? 'font-semibold text-foreground' : 'text-muted-foreground',
        )}
      >
        {label}
      </span>
      <span
        className={cn(
          'font-display tabular-nums tracking-tight text-foreground',
          bold ? 'text-lg font-semibold' : 'text-[13px]',
        )}
      >
        {value}
      </span>
    </div>
  );
}

function formatDateShort(iso: string): string {
  // "Tue, Apr 28" — mirrors what mobile's PlanCard renders. Locale-
  // fixed so the surface reads consistently across browsers; parse
  // tolerant of both `YYYY-MM-DD` (model-provided) and full ISO.
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}
